/**
 * 분할 생성(1점검 → N문서) **진입 오케스트레이션** — 결정 2026-09-01 §③ / 2026-09-07 F-1.
 *
 * 진입점이 둘이 되면서(점검 상세의 문서 유형 선택 모달 + `/reports/new` 위저드) 같은 절차가
 * 두 벌이 될 자리라 여기 모았다: **계획 조회 → 그룹 선택 모달 → 생성 → 결과 안내**.
 * 두 벌이면 "고를 수 있는 것"과 "만들어지는 것"이 진입점마다 갈린다.
 *
 * 호스트가 계속 소유하는 것(둘이 실제로 다르다):
 *  - **로딩·에러 표시** — 상세 모달은 전용 로딩 화면 + 재시도 배너, 위저드는 페이지 배너다.
 *    그래서 계획 조회 실패는 삼키지 않고 **throw**한다.
 *  - **이동 경로**(`from`/`fromLabel`·통합 갈래의 URL) — 상세는 단일 점검 + AI 사전 생성,
 *    위저드는 다중 점검 URL이다.
 *  - **겹침 층**(`layer`) — 여는 쪽이 정한다([[feedback-modal-layer-belongs-to-opener-not-component]]).
 */
import { useState, type ReactElement } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ReportTemplate,
  SplitCreateGroupSelection,
  SplitExistingReport,
  SplitPlan,
} from '@safetyhub/shared';
import { createSplitReports, getSplitPlan } from '../../lib/report-split';
import { extractApiError } from '../../lib/api';
import { useToastStore } from '../../stores/toast';
import { MODAL_LAYER } from '../modalLayers';
import { SplitGroupSelectModal } from './SplitGroupSelectModal';

/**
 * 이 템플릿이 분할 생성(1점검 → N문서)을 선언했는가 — `policy.split` 유무.
 *
 * 판정 정본은 서버(registry provider 등록·대상 행 존재까지 본다)이고, 이건 그 앞의 **보수적**
 * 게이트다: 선언이 없으면 서버도 반드시 `plan: null`이라 계획 조회를 건너뛰어도 결과가 같다.
 * 반대로 선언이 있는데 서버가 null을 내는 경우(미등록 provider·행 0건)는 조회 후에 걸러진다.
 *
 * ⚠️ **계획 조회 게이트 전용**이다. 중복 잠금 축(`allowsMultiple`)에 쓰지 말 것 — 넓혀 놓으면
 * `duplicatePolicy:'allow'`(분할 아님) 템플릿까지 매번 계획을 묻게 된다.
 *
 * ⚠️ 호출부가 이 술어로 **먼저 거른 뒤** `begin`을 부른다. `begin` 안으로 옮기면 분할을
 * 선언하지 않은 템플릿에서도 호스트가 로딩을 켜게 되어(결과를 봐야 끌 수 있으므로) 스피너가
 * 한 번 번쩍인다.
 */
export function isSplitCapable(tpl: ReportTemplate): boolean {
  return !!tpl.policy?.split?.provider;
}

/** 계획 조회·생성이 함께 쓰는 테넌트 축. 점검은 배열이다(위저드는 여러 건을 고를 수 있다). */
export interface SplitEntryScope {
  companyId: string;
  siteId: string;
  inspectionIds: string[];
}

export interface UseSplitGroupEntryOptions {
  /**
   * 통합(combined) 확정 — "분할하지 않음 = 1건"이라 호스트의 **종전 1문서 경로 그대로**다
   * (결정 §③-1). 갈래를 여기서 만들면 진입점마다 AI 사전 생성 유무가 갈린다.
   */
  onCombined: (template: ReportTemplate, entryGuidance?: string) => void;
  /** 분할 생성 성공 — 첫 초안의 작성 화면으로 보낸다(2026-09-06 A안). */
  onCreated: (template: ReportTemplate, firstReportId: string) => void;
  /** 전건 skip(경합) — 새로 연 초안이 없으니 열 곳도 없다. 안내 토스트는 훅이 이미 띄웠다. */
  onNoneCreated: () => void;
  /**
   * 생성 실패 사유 — 호스트 배너에 남긴다(토스트는 훅이 띄운다).
   *
   * ⚠️ 토스트가 이 경로의 **유일한** 도달 수단이다: 실패해도 그룹 선택 모달을 닫지 않으므로
   * (고른 배치를 잃지 않게) 호스트 배너는 그 모달 뒤에 가린다. 배너는 사용자가 모달을 닫은
   * 뒤에도 사유가 남게 하는 몫이다.
   */
  onCreateError?: (message: string) => void;
  /** 겹침 층 — 미지정 시 모달 기본값(`MODAL_LAYER.sub`). 페이지에서 직접 열면 `root`. */
  layer?: string;
}

export interface SplitGroupEntry {
  /**
   * 계획을 물어 그룹 선택 모달을 연다.
   *
   * - `'opened'`: 모달을 띄웠다 — 호스트는 여기서 멈춘다.
   * - `'not-split'`: `plan: null` = 이 템플릿·컨텍스트는 분할하지 않는다(정책 오타·미등록
   *   provider·대상 행 0건이 전부 여기로 접힌다) — 호스트가 종전 1문서 경로로 계속 간다.
   *
   * ⚠️ 조회 실패는 **throw**다. 여기서 종전 흐름으로 흘려보내면 사용자가 고를 기회 없이
   * **1건이 만들어진다** — 무엇이 생기는지가 달라지므로 조용히 넘기지 않는다.
   */
  begin: (
    template: ReportTemplate,
    scope: SplitEntryScope,
    entryGuidance?: string,
  ) => Promise<'opened' | 'not-split'>;
  /** 생성 진행 중 — 모달이 닫기 수단을 잠근다. 호스트도 자기 버튼을 잠그고 싶을 때 읽는다. */
  isCreating: boolean;
  /** 모달 엘리먼트 — 호스트 JSX에 그대로 둔다(열려 있지 않으면 null). */
  modal: ReactElement | null;
}

export function useSplitGroupEntry(options: UseSplitGroupEntryOptions): SplitGroupEntry {
  const { onCombined, onCreated, onNoneCreated, onCreateError, layer } = options;
  const { showToast } = useToastStore();
  // 형제 목록 캐시 무효화용(F-9) — 아래 handleConfirmSplit 주석 참조.
  const queryClient = useQueryClient();

  // 계획은 진입 시점에 서버에서 받아 그대로 들고 있는다(템플릿 카드마다 미리 조회하지 않는다).
  // ⚠️ 계획 조회는 읽기처럼 생겼지만 provider가 운영 데이터에 쓴다(TBM classifier의 TbmMeta
  // upsert) — 폴링·useEffect로 감싸지 말 것. 호출은 사용자의 명시적 조작 1회여야 한다.
  const [target, setTarget] = useState<{
    template: ReportTemplate;
    scope: SplitEntryScope;
    plan: SplitPlan;
    existing: SplitExistingReport[];
    /** 진입 전 사용자 입력 — 통합으로 빠질 때 종전 경로에 그대로 넘긴다. */
    entryGuidance?: string;
  } | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  const begin = async (
    template: ReportTemplate,
    scope: SplitEntryScope,
    entryGuidance?: string,
  ): Promise<'opened' | 'not-split'> => {
    const { plan, existing } = await getSplitPlan({
      companyId: scope.companyId,
      siteId: scope.siteId,
      templateKey: template.key,
      inspectionIds: scope.inspectionIds,
    });
    if (!plan) return 'not-split';
    setTarget({ template, scope, plan, existing, entryGuidance });
    return 'opened';
  };

  /**
   * 분할 그룹 선택 확정(개별) — 고른 그룹마다 DRAFT 1건.
   *
   * 생성 후 **첫 초안의 작성 화면으로 바로 들어간다**(2026-09-06 사용자 결정 A안). 종전에는
   * 문서 목록으로 보냈는데, 그 근거였던 "한 건을 열면 나머지 N-1건이 안 보인다"는 형제 칩이
   * 작성 페이지까지 확장되면서 사라졌다 — 이제 작성 화면 안에서 나머지 형제로 순회한다.
   * 통합(combined)이 「작성 시작」으로 작성 페이지에 직행하는 것과도 대칭이 맞는다.
   */
  const handleConfirmSplit = async (groups: SplitCreateGroupSelection[]) => {
    if (!target || isCreating) return;
    const { template, scope } = target;
    setIsCreating(true);
    try {
      // content는 싣지 않는다 — 여기 본문 행을 실으면 서버의 그룹별 행 제한을 덮어써
      // N건이 전부 같아진다(결정 §⑤의 공통 기본값 패널은 후속 범위).
      const result = await createSplitReports({
        companyId: scope.companyId,
        siteId: scope.siteId,
        templateKey: template.key,
        inspectionIds: scope.inspectionIds,
        groups,
      });

      // skip 사유는 이제 DUPLICATE 하나뿐이다 — `'EMPTY'`(담을 행 0건) 분기는 제거했다(§③-2).
      // 요청이 담을 행을 id로 명시하고(빈 배열 거부) 없는 id는 400이라, 서버에서 "0건이 된 그룹"이
      // 성립하지 않는다. 살려 두면 영영 도달하지 않는 문구를 유지보수하게 된다.
      const duplicate = result.skipped.filter((s) => s.reason === 'DUPLICATE').length;
      const notes: string[] = [];
      if (duplicate > 0) notes.push(`${duplicate}건은 이미 만들어져 있어 건너뛰었습니다`);

      if (result.created.length === 0) {
        // 전건 skip은 오류가 아니라 안내다(모달을 열어 둔 사이 다른 사람이 만든 경우 등).
        showToast(
          notes.length > 0
            ? `새로 만든 초안이 없습니다. ${notes.join(', ')}.`
            : '새로 만든 초안이 없습니다.',
          'warning',
        );
      } else {
        showToast(
          notes.length > 0
            ? `${result.created.length}건의 초안을 만들었습니다. ${notes.join(', ')}.`
            : `${result.created.length}건의 초안을 만들었습니다.`,
          notes.length > 0 ? 'warning' : 'success',
        );
      }

      // 형제 목록 캐시 무효화(F-9, 2026-09-07 검수 적발) — 같은 묶음에 **추가 생성**하는 경우
      // (한 건 만들고 뒤로 가서 남은 그룹을 또 만드는 흐름), 먼저 만든 문서의 작성 화면이
      // 이미 형제 목록을 캐시해 두었다. 캐시 키(회사·점검·양식·배치)는 새 문서와 **같아서**
      // staleTime(5분) 안에는 재조회가 생략되고, 새 문서의 작성 화면에 형제 칩이 뜨지 않는다
      // (서버는 활성 묶음 합류로 같은 splitBatchId를 줬는데 화면만 모르는 상태).
      if (result.created.length > 0) {
        queryClient.invalidateQueries({ queryKey: ['siblingReports'] });
      }

      setTarget(null);
      if (result.created.length === 0) {
        onNoneCreated();
        return;
      }
      // 첫 건 = 요청 그룹 순서의 첫 생성분. 서버(report-split.service `createSplit`)는 요청
      // `groups`를 순서 그대로 정규화해 루프를 돌며 `created`에 push하므로, 이 배열 순서는
      // 사용자가 분할 모달에서 본 그룹 순서와 같다(중복 skip분만 빠진다).
      onCreated(template, result.created[0].reportId);
    } catch (err) {
      const apiError = extractApiError(err);
      const message = apiError.message || '초안 생성에 실패했습니다.';
      // 실패 시 `target`을 지우지 않아 그룹 선택 모달이 그대로 떠 있다(고른 배치 보존).
      // 그래서 호스트 배너는 그 뒤에 가리고, 토스트가 유일한 도달 수단이다.
      showToast(message, 'error');
      onCreateError?.(message);
    } finally {
      setIsCreating(false);
    }
  };

  const modal = target ? (
    <SplitGroupSelectModal
      isOpen={true}
      templateName={target.template.name}
      plan={target.plan}
      existing={target.existing}
      isSubmitting={isCreating}
      onClose={() => setTarget(null)}
      onConfirmSplit={handleConfirmSplit}
      onCombined={() => {
        // 통합 = 분할하지 않음(1건) → 호스트의 종전 작성 진입 그대로(결정 §③-1)
        const current = target;
        setTarget(null);
        onCombined(current.template, current.entryGuidance);
      }}
      layer={layer ?? MODAL_LAYER.sub}
    />
  ) : null;

  return { begin, isCreating, modal };
}
